import Decimal from 'decimal.js'
import { convert, type IcalObject } from 'ical2json'
import { DateTime } from 'luxon'
import { useContext, useMemo } from 'react'
import { BookingContext } from './context'

type IcsRoot = IcalObject & { VCALENDAR: IcalObject[] }
type VCalendarBlock = IcalObject & { VEVENT: IcalObject[] }

export function useBookingResultIcsUrls() {
    const { bookingEvent, bookingResult } = useContext(BookingContext)

    if (!bookingEvent || !bookingResult) {
        throw new Error('Booking event or ICS is missing')
    }

    return useIcsUrls(bookingResult.ics, bookingEvent.startDatetime, bookingEvent.endDatetime)
}

export function useIcsUrls(ics: string, startDatetime: DateTime<true>, endDatetime: DateTime<true>) {
    const root = convert(ics) as IcsRoot
    const calendar = root.VCALENDAR[0] as VCalendarBlock
    const icsEvent = calendar.VEVENT[0] as IcalObject

    const summary = encodeURIComponent(icsEvent.SUMMARY.toString())
    const description = encodeURIComponent(icsEvent.DESCRIPTION.toString())
    const start = startDatetime.toFormat("yyyyMMdd'T'HHmmss")
    const end = endDatetime.toFormat("yyyyMMdd'T'HHmmss")
    const location = encodeURIComponent(
        icsEvent.LOCATION.toString().replaceAll('\\n', ' ').replaceAll('\\', '').replaceAll('Vor Ort ', '')
    )

    return {
        genericIcsUrl: `data:text/calendar;charset=utf8,${ics}`,
        googleIcsUrl: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${summary}&dates=${start}/${end}&details=${description}&location=${location}`,
        yahooIcsUrl: `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${summary}&st=${start}&et=${end}&desc=${description}&in_loc=${location}`
    }
}

export function useParsedPrice(price: string) {
    return useMemo(() => {
        const safe = price.trim() !== '' ? price : '0'
        try {
            const priceNumber = new Decimal(safe).toNumber()
            const priceLabel = safe.endsWith('.00') ? safe.split('.')[0] : safe.replace('.', ',')
            return { price: priceNumber, priceLabel }
        } catch {
            return { price: 0, priceLabel: '0' }
        }
    }, [price])
}
